import { ClientAccount, DiscountRule } from '../types';

/**
 * Finds the best-matching discount rule for an account, based on its
 * visitCount/totalSpend vs each rule's metric+threshold, and scoped to
 * the given outlet (global rules with outletId=null/undefined always
 * apply; outlet-specific rules only apply when outletId matches).
 * "Best" = largest discount amount on the given subtotal.
 * Returns null if no rule matches, the account is exempt, or rules is empty.
 */
export function getBestDiscountRule(
  account: ClientAccount | null | undefined,
  rules: DiscountRule[],
  subtotal: number,
  outletId?: number | null
): DiscountRule | null {
  if (!account || account.exemptFromAutoDiscount) return null;
  if (!rules || rules.length === 0) return null;

  const visits = account.visitCount || 0;
  const spend = account.totalSpend || 0;

  const eligible = rules.filter(r => {
    // Outlet scoping: supports both camelCase (outletId) and snake_case (outlet_id) from DB
    const ruleOutlet = r.outletId !== undefined ? r.outletId : (r as any).outlet_id;
    const outletMatches = ruleOutlet == null || ruleOutlet === outletId;
    if (!outletMatches) return false;

    if (r.metric === 'visits') return visits >= r.threshold;
    return spend >= r.threshold;
  });
  if (eligible.length === 0) return null;

  let best: DiscountRule | null = null;
  let bestAmount = -1;
  for (const rule of eligible) {
    const amount = rule.type === 'percent'
      ? (subtotal * (rule.value || 0)) / 100
      : Math.min(rule.value || 0, subtotal);
    if (amount > bestAmount) {
      bestAmount = amount;
      best = rule;
    }
  }
  return best;
}

/** Computes the discount amount (currency value) for the best-matching rule. */
export function computeAutoDiscount(
  account: ClientAccount | null | undefined,
  rules: DiscountRule[],
  subtotal: number,
  outletId?: number | null
): number {
  const rule = getBestDiscountRule(account, rules, subtotal, outletId);
  if (!rule) return 0;
  if (rule.type === 'percent') {
    return Math.max(0, (subtotal * (rule.value || 0)) / 100);
  }
  return Math.max(0, Math.min(rule.value || 0, subtotal));
}

/** Human-readable label for a rule. */
export function describeDiscountRule(rule: DiscountRule): string {
  if (rule.label && rule.label.trim()) {
    return rule.label.trim();
  }
  const metricLabel = rule.metric === 'visits' ? `${rule.threshold}+ visits` : `Spend ≥ ${rule.threshold}`;
  const valueLabel = rule.type === 'percent' ? `${rule.value}% off` : `${rule.value} off`;
  return `${metricLabel} → ${valueLabel}`;
}